import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageShell from '../components/PageShell.jsx';
import Seo from '../components/Seo.jsx';
import UniversityLogo from '../components/UniversityLogo.jsx';
import { useCompare } from '../components/CompareContext.js';
import { useLeadModal } from '../components/LeadModalContext.js';
import { getLeadIntentCopy, programInterestLabel } from '../content/index.js';
import { formatDurationMonths, formatMoney, locationLabel } from '../utils/format.js';

const ROWS = [
  { key: 'level', label: 'Level', render: (c) => c.levelName || c.levelCode },
  { key: 'duration', label: 'Duration', render: (c) => formatDurationMonths(c.durationMonths) },
  { key: 'mode', label: 'Mode', render: (c) => (c.mode ? c.mode.charAt(0).toUpperCase() + c.mode.slice(1) : '—') },
  {
    key: 'fee',
    label: 'Total fee (indicative)',
    render: (c) => formatMoney(c.totalFee, c.currency) || 'Ask a counsellor',
    emphasise: true
  },
  {
    key: 'semester',
    label: 'Per semester',
    render: (c) => formatMoney(c.semesterFee, c.currency) || '—'
  },
  { key: 'annual', label: 'Per year', render: (c) => formatMoney(c.annualFee, c.currency) || '—' },
  { key: 'eligibility', label: 'Eligibility', render: (c) => c.eligibility },
  { key: 'semesters', label: 'Semesters', render: (c) => c.semesters },
  { key: 'credits', label: 'Credits', render: (c) => c.credits },
  { key: 'hours', label: 'Weekly commitment', render: (c) => `${c.hoursPerWeek} hours` },
  {
    key: 'location',
    label: 'Location',
    render: (c) => locationLabel(c.university) || '—'
  },
  {
    key: 'careers',
    label: 'Career paths',
    render: (c) => (c.careerPaths || []).join(', ')
  }
];

export default function ComparePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { remove, items } = useCompare();
  const { openLeadModal } = useLeadModal();
  const feesCopy = getLeadIntentCopy('fees');
  const [state, setState] = useState({ loading: true, error: null, courses: [] });

  const slugs = useMemo(
    () =>
      (searchParams.get('c') || '')
        .split(',')
        .map((slug) => slug.trim())
        .filter(Boolean)
        .slice(0, 3),
    [searchParams]
  );
  const slugKey = slugs.join(',');

  useEffect(() => {
    if (!slugKey) {
      setState({ loading: false, error: null, courses: [] });
      return undefined;
    }
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: null }));
    fetch(`/api/compare?slugs=${encodeURIComponent(slugKey)}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error('Failed');
        return res.json();
      })
      .then((payload) => {
        setState({ loading: false, error: null, courses: payload.data?.courses || [] });
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setState({ loading: false, error: 'Unable to load the comparison. Please try again.', courses: [] });
      });
    return () => controller.abort();
  }, [slugKey]);

  function removeColumn(slug) {
    remove(slug);
    const next = slugs.filter((item) => item !== slug);
    setSearchParams(next.length ? { c: next.join(',') } : {});
  }

  function getFees(course) {
    openLeadModal({
      intent: 'fees',
      leadSource: `compare_fees_${course.slug}`,
      preferredProgram: programInterestLabel(course.name, course.university.name)
    });
  }

  const { loading, error, courses } = state;
  const hasTwoOrMore = courses.length >= 2;

  return (
    <PageShell>
      <Seo
        title="Compare online programmes side by side | BharatShiksha"
        description="Compare fees, duration, eligibility and more for up to three online programmes."
      />
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <span>Compare programmes</span>
        </div>
      </div>

      <section className="section compare-page">
        <div className="container">
          <header className="section-header">
            <h1 className="compare-title">Compare programmes</h1>
            <p>Fee figures are indicative; confirm the current admission-cycle fee with a counsellor.</p>
          </header>

          {loading && <p className="status-text">Loading comparison...</p>}
          {error && <p className="status-text error">{error}</p>}

          {!loading && !error && courses.length === 0 && (
            <div className="compare-empty">
              <p>
                {items.length
                  ? 'Select at least two programmes to compare.'
                  : 'You have not selected any programmes yet.'}
              </p>
              <Link className="cta-button" to="/#universities">
                Browse universities
              </Link>
            </div>
          )}

          {!loading && courses.length > 0 && (
            <>
              {!hasTwoOrMore && (
                <p className="search-notice">
                  Add at least one more programme from any university page to compare side by side.
                </p>
              )}
              <div className="compare-table-wrap">
                <table className="compare-table">
                  <thead>
                    <tr>
                      <th scope="col" className="compare-corner">
                        Programme
                      </th>
                      {courses.map((course) => (
                        <th scope="col" key={course.slug}>
                          <div className="compare-col-head">
                            <UniversityLogo
                              slug={course.university.slug}
                              name={course.university.name}
                              logoUrl={course.university.logoUrl}
                            />
                            <Link
                              to={`/universities/${course.university.slug}/courses/${course.slug}`}
                              className="compare-course-link"
                            >
                              {course.name}
                            </Link>
                            <span className="college-location">{course.university.name}</span>
                            <button
                              type="button"
                              className="link-button"
                              onClick={() => removeColumn(course.slug)}
                            >
                              Remove
                            </button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ROWS.map((row) => (
                      <tr key={row.key} className={row.emphasise ? 'emphasise' : undefined}>
                        <th scope="row">{row.label}</th>
                        {courses.map((course) => (
                          <td key={course.slug}>{row.render(course)}</td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <th scope="row">Next step</th>
                      {courses.map((course) => (
                        <td key={course.slug}>
                          <button
                            type="button"
                            className="cta-button lead-intent-btn"
                            data-lead-intent="fees"
                            onClick={() => getFees(course)}
                          >
                            {feesCopy.cta || 'Get course fees'}
                          </button>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
}
